/**
 * Type Guards for Automation Flow Nodes
 *
 * Provides type-safe runtime checks for node data types. These guards enable
 * TypeScript to narrow types based on runtime conditions, ensuring correct
 * property access on different node types.
 *
 * Usage:
 * ```typescript
 * if (isSwitchNodeData(node.data)) {
 *   // TypeScript knows node.data has automationStep with cases array
 *   const cases = node.data.automationStep.cases;
 * }
 * ```
 */

import type * as Api from '@ViewModels';
import type { BaseNodeData } from './nodes';
import type { AutomationStepNodeData } from './stepNodes';

/**
 * Type representing node data that is a switch automation step.
 * Provides full type safety for accessing switch-specific properties.
 */
export type SwitchNodeData = AutomationStepNodeData & {
	automationStep: Api.ISwitchAutomationStep;
	outputHandles: string[];
};

/**
 * Type guard to check if node data represents a switch automation step.
 *
 * Validates that the node:
 * 1. Has an automationStep property
 * 2. The step type is 'SwitchAutomationStep'
 * 3. Has a cases array (required for switch logic)
 * 4. Has outputHandles array (required for branch connections)
 *
 * @param data - Node data to check
 * @returns True if the data represents a switch node with proper structure
 *
 * @example
 * ```typescript
 * const node = nodes.find(n => n.id === switchNodeId);
 * if (node && isSwitchNodeData(node.data)) {
 *   // Access switch-specific properties safely
 *   const caseCount = node.data.automationStep.cases?.length ?? 0;
 *   const handleCount = node.data.outputHandles.length;
 * }
 * ```
 */
export function isSwitchNodeData(data: BaseNodeData): data is SwitchNodeData {
	const stepData = data as AutomationStepNodeData;
	const automationStep = stepData.automationStep as Api.ISwitchAutomationStep | undefined;

	return (
		automationStep !== undefined &&
		automationStep._type === 'SwitchAutomationStep' &&
		Array.isArray(automationStep.cases) &&
		Array.isArray(stepData.outputHandles) &&
		stepData.outputHandles.length > 0
	);
}

/**
 * Less strict type guard that only checks for switch step type.
 * Use when you only need to verify it's a switch node but don't need
 * to access the cases array (e.g., for layout positioning).
 *
 * @param data - Node data to check
 * @returns True if the data represents a switch node
 */
export function isSwitchStepNode(data: BaseNodeData): data is AutomationStepNodeData & {
	automationStep: { _type: 'SwitchAutomationStep' };
	outputHandles: string[];
} {
	const stepData = data as AutomationStepNodeData;
	const automationStep = stepData.automationStep as { _type?: string } | undefined;

	return (
		automationStep !== undefined &&
		automationStep._type === 'SwitchAutomationStep' &&
		Array.isArray(stepData.outputHandles) &&
		stepData.outputHandles.length > 0
	);
}

/**
 * Type guard to check if node data has an estimatedHeight property.
 * Used by layout calculations to determine node dimensions before rendering.
 *
 * @param data - Node data to check
 * @returns True if the data has a numeric estimatedHeight
 */
export function hasEstimatedHeight(data: BaseNodeData): data is BaseNodeData & { estimatedHeight: number } {
	return typeof (data as { estimatedHeight?: unknown }).estimatedHeight === 'number';
}

/**
 * Type guard to check if node data is an automation step node.
 * Accepts unknown to support runtime validation at component boundaries
 * where data may not yet be typed (e.g., React Flow NodeProps).
 *
 * @param data - Node data to check
 * @returns True if the data has stepType and nodeId properties
 */
export function isAutomationStepNodeData(data: unknown): data is AutomationStepNodeData {
	if (!data || typeof data !== 'object') {
		return false;
	}
	const stepData = data as Partial<AutomationStepNodeData>;
	return stepData.stepType !== undefined && stepData.nodeId !== undefined;
}

/**
 * Type guard to check if a node is a trigger node.
 *
 * @param node - Node to check
 * @returns True if the node type is 'trigger'
 */
export function isTriggerNode(node: { type?: string }): boolean {
	return node.type === 'trigger';
}
